// UserModal.tsx
import React, { useState } from 'react';
import { Box, Typography, Modal, Select, MenuItem, FormControl, InputLabel, Input, FormGroup, Button, TextField, FormLabel } from '@mui/material';
import { CreateUser, RoleOption } from './UserType';
import { z } from 'zod';

const userSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    username: z.string().min(1, 'Username is required'),
    roleId: z.enum(['super_admin', 'admin', 'executive']),
  });

interface UserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (user: CreateUser) => Promise<void>;
  initialValues?: CreateUser;
  isEdit?: boolean;
}

export const UserModal: React.FC<UserModalProps> = ({ open, onClose, onSubmit, initialValues, isEdit }) => {
    const [user, setUser] = useState<CreateUser>(initialValues || {
        name: '',
        username: '',
        roleId: 'executive'
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const result = userSchema.safeParse(user);
        if (!result.success) {
            // Map zod errors to field errors
            const fieldErrors: { [key: string]: string } = {};
            result.error.errors.forEach(err => {
            if (err.path[0]) fieldErrors[err.path[0]] = err.message;
            });
            setErrors(fieldErrors);
            return;
        }
        setErrors({});

        try {
            await onSubmit(result.data);
            onClose();
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <Modal open={open} onClose={onClose}>
            <Box
            component="form"
            sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', boxShadow: 24, p: 4 }}
            onSubmit={handleSubmit}
            >
                <Typography variant="h6">Add User</Typography>
                <FormGroup>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <FormLabel htmlFor="name">Name</FormLabel>
                        <TextField
                        id="name"
                        type="name"
                        name="name"
                        placeholder="your name"
                        autoComplete="name"
                        autoFocus
                        required
                        // fullWidth
                        variant="outlined"
                        value={user.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUser({ ...user, name: e.target.value })}
                        error={!!errors.name}
                        helperText={errors.name}
                        color={errors.name ? 'error' : 'primary'}
                        />
                    </FormControl>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <FormLabel htmlFor="username">Username</FormLabel>
                        <TextField
                        id="username"
                        type="username"
                        name="username"
                        placeholder="your username"
                        autoComplete="username"
                        autoFocus
                        required
                        // fullWidth
                        variant="outlined"
                        value={user.username}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUser({ ...user, username: e.target.value })}
                        error={!!errors.username}
                        helperText={errors.username}
                        color={errors.username ? 'error' : 'primary'}
                        />
                    </FormControl>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <FormLabel htmlFor="role">Role</FormLabel>
                        <Select
                            id="role"
                            name="role"
                            value={user.roleId}
                            onChange={(e: any) => setUser({ ...user, roleId: e.target.value as RoleOption })}
                        >
                            <MenuItem value={'super_admin'}>Super Admin</MenuItem>
                            <MenuItem value={'admin'}>Admin</MenuItem>
                            <MenuItem value={'executive'}>Executive</MenuItem>
                        </Select>
                    </FormControl>
                </FormGroup>
                <Box mt={2} display="flex" justifyContent="flex-end">
                <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
                <Button type="submit" variant="contained" color="primary">{isEdit ? 'Update' : 'Add'}</Button>
                </Box>
            </Box>
        </Modal>
    );
}

